// Shared write-conflict rules for the vendor item write surfaces (HUBX-8474, from
// HUBX-8439).
//
// The COMPARISON only: which members are covered, how either side is flattened so they can
// be compared, and which members a 412 should mark. Resolving the conflict — re-reading the
// item and choosing per member (item-write-resolve.rules, HUBX-8466) — is the surface's job,
// because each one holds its state differently.
//
// No user-facing copy here: the single sentence a conflict shows lives in
// item-write-messages.ts with the rest of it.

import { IConflictField, IConflictMark, ICreateItemDiscountQty, ILookupAttribute } from '../item.models';

/** The three attribute lookups the comparison resolves ids through. Undefined while
 *  /lookups/attributes is in flight, and then the attribute members compare as null on both
 *  sides — equal, so unmarked, which is the right answer when nothing is known yet. */
export interface IAttributeLookups {
  condition?: ILookupAttribute;
  warranty?: ILookupAttribute;
  packaging?: ILookupAttribute;
}

/** Every member the comparison covers, with what it is called and where it is edited.
 *
 *  `writable` is the ADR-0011 path that decides whether the member may travel in this
 *  caller's body. One this caller cannot write is still marked — the other writer moved
 *  it — and the resolve popup offers it as Current only, because echoing the value read
 *  would send something the PUT refuses and turn the next Save into a 403. `tags` has no
 *  control and no path at all: nothing here can edit it, so what the body carries is an
 *  echo rather than an intent, and the popup never lists it.
 *
 *  `vendorItemCode` is absent: immutable for everyone, so it cannot diverge. The two master
 *  fields follow the aggregate (`attributes.*`), exactly as applyWritability decides their
 *  enabled state — their real permission depends on a destination master that only
 *  /items/resolutions can judge.
 *
 *  `isArchived` carries a `control` name that matches no FormControl: the Archive action
 *  holds that state, not the form. It is here because `control` is only ever a LOOKUP key —
 *  conflictFor matches marks by it and nothing dereferences it against the form — so the
 *  archived ring is found the same way as every other. */
export const CONFLICT_FIELDS: IConflictField[] = [
  { key: 'hubxItemCode', label: 'XID', control: 'hubxItemCode', writable: 'hubxItemCode' },
  { key: 'mpn', label: 'MPN', control: 'mpn', writable: 'attributes.*' },
  { key: 'description', label: 'Description', control: 'description', writable: 'description' },
  { key: 'manufacturerId', label: 'Manufacturer', control: 'manufacturerId', writable: 'attributes.*' },
  { key: 'categoryId', label: 'Category', control: 'categoryId', writable: 'categoryId' },
  { key: 'unitPrice', label: 'Price', control: 'unitPrice', writable: 'unitPrice' },
  { key: 'unitPurchasePrice', label: 'Cost', control: 'cost', writable: 'unitPurchasePrice' },
  { key: 'availability', label: 'Available', control: 'availability', writable: 'availability' },
  { key: 'minimumOrderQty', label: 'MOQ', control: 'moq', writable: 'minimumOrderQty' },
  { key: 'maximumOrderQty', label: 'MXQ', control: 'mxq', writable: 'maximumOrderQty' },
  { key: 'leadTimeDays', label: 'ETA', control: 'leadTimeDays', writable: 'leadTimeDays' },
  { key: 'exwId', label: 'EXW', control: 'exwId', writable: 'exwId' },
  { key: 'specialNotes', label: 'Special Notes', control: 'specialNotes', writable: 'specialNotes' },
  { key: 'restrictions', label: 'Restrictions', control: 'restrictions', writable: 'restrictions' },
  { key: 'effectiveStatus', label: 'Status', control: 'skuStatus', writable: 'status' },
  { key: 'isArchived', label: 'Archived', control: 'isArchived', writable: 'isArchived' },
  { key: 'todaysDeals', label: "Today's Deals", control: 'todaysDeals', writable: 'todaysDeals' },
  { key: 'attributes.Condition', label: 'Condition', control: 'condition', writable: 'attributes.Condition' },
  { key: 'attributes.Warranty', label: 'Warranty', control: 'warranty', writable: 'attributes.Warranty' },
  { key: 'attributes.Packaging', label: 'Packaging', control: 'packaging', writable: 'attributes.Packaging' },
  { key: 'discountQtys', label: 'Discount quantities', control: 'discountQtys', writable: 'discountQtys' },
  { key: 'tags', label: 'Tags' }
];

export function attributeValue(attributes: ICreateItemAttributeLike[] | undefined, lookup?: ILookupAttribute): unknown {
  if (!lookup) {
    return null;
  }
  return (attributes ?? []).find(a => a.attributeId === lookup.id)?.value ?? null;
}

/** The attribute shape both sides carry — the response's and the request's are the same. */
interface ICreateItemAttributeLike {
  attributeId: string;
  value?: string;
}

/** Reduce either side to one flat map keyed by request member, so the two can be compared
 *  without a per-member branch. It works on both `ItemResponse` and the built request
 *  because the round-trip invariant (§4.2) makes their member names the same — which is
 *  exactly why `currentState` is the GET representation.
 *
 *  Status is the one member whose name differs: the response calls the projection
 *  `effectiveStatus` and the request carries it as `status` (ADR-0009 Rule 9). The caller
 *  passes the value in for either side. */
export function comparable(
  source: { [key: string]: any },
  effectiveStatus: string,
  lookups: IAttributeLookups
): Map<string, unknown> {
  const map = new Map<string, unknown>();
  const norm = (v: unknown) => (v === undefined || v === '' ? null : v);

  for (const field of CONFLICT_FIELDS) {
    switch (field.key) {
      case 'effectiveStatus':
        map.set(field.key, norm(effectiveStatus));
        break;
      case 'attributes.Condition':
        map.set(field.key, attributeValue(source['attributes'], lookups.condition));
        break;
      case 'attributes.Warranty':
        map.set(field.key, attributeValue(source['attributes'], lookups.warranty));
        break;
      case 'attributes.Packaging':
        map.set(field.key, attributeValue(source['attributes'], lookups.packaging));
        break;
      case 'discountQtys':
        map.set(field.key, (source['discountQtys'] ?? [])
          .map((t: ICreateItemDiscountQty) => `${t.fromQty}:${t.price}`).join('|'));
        break;
      case 'tags':
        map.set(field.key, [...(source['tags'] ?? [])].sort().join('|'));
        break;
      case 'todaysDeals':
        // The ONE member where absent does not mean null. The PUT reads an absent
        // `todaysDeals` as "do not touch", so a body that omits it overwrites nothing and
        // there is no conflict to report. Every other member is full state, where absent IS
        // a removal and therefore a real write.
        map.set(field.key, source['todaysDeals'] === undefined ? undefined : !!source['todaysDeals']);
        break;
      default:
        map.set(field.key, norm(source[field.key]));
    }
  }
  return map;
}

/** What a 412 should mark. THE rule, and the whole of it:
 *
 *    mark f  <=>  current(f) != origin(f)  AND  mine(f) != current(f)
 *
 *  The first condition says somebody else moved it since this client read the item; the
 *  second says saving would move it again. Together they select exactly the members where a
 *  Save would overwrite somebody's change — no more, and no less.
 *
 *  Both are load-bearing. Drop the first and a field the user edited alone gets marked,
 *  where the only "divergence" is the user's own edit. Drop the second and the case where
 *  BOTH writers landed on the same value gets marked, which rings a field over a difference
 *  that does not exist.
 *
 *  The marks are rings only. Nothing is adopted into the form any more (HUBX-8466): the
 *  item cannot be saved until the resolve popup answers every member against the item as
 *  re-read, and a member this caller may not write is Current there. */
export function diffConflicts(
  before: Map<string, unknown>,
  current: Map<string, unknown>,
  mine: Map<string, unknown>
): Map<string, IConflictMark> {
  const marks = new Map<string, IConflictMark>();

  for (const field of CONFLICT_FIELDS) {
    const theirs = current.get(field.key);
    if (theirs === before.get(field.key) || mine.get(field.key) === theirs) {
      continue;
    }

    marks.set(field.key, {
      key: field.key,
      label: field.label,
      control: field.control
    });
  }

  return marks;
}

/** The mark on one control, or null. Surfaces look marks up by CONTROL name, since that is
 *  what a template has in hand. */
export function conflictFor(marks: Map<string, IConflictMark>, control: string): IConflictMark | null {
  for (const mark of marks.values()) {
    if (mark.control === control) {
      return mark;
    }
  }
  return null;
}
